import React, { useState, useEffect } from 'react';
import { Upload, Info, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { getFileUrl } from '../../lib/api';

const STAT_ICON_OPTIONS = [
  { value: 'building', label: 'Bina' },
  { value: 'hardhat', label: 'Baret / İnşaat' },
  { value: 'trending', label: 'Yükseliş / Grafik' },
  { value: 'users', label: 'Kullanıcılar' },
  { value: 'award', label: 'Ödül' },
  { value: 'home', label: 'Ev' },
  { value: 'check', label: 'Onay' },
  { value: 'calendar', label: 'Takvim' },
];

const DEFAULT_STATS = {
  enabled: true,
  items: [
    { number: '20+', label: 'Yıllık Tecrübe', icon: 'building' },
    { number: '50+', label: 'Tamamlanan Proje', icon: 'hardhat' },
    { number: '100%', label: 'Müşteri Memnuniyeti', icon: 'trending' },
    { number: '10+', label: 'Devam Eden Proje', icon: 'building' },
  ],
};

const DEFAULT_CTA = {
  enabled: true,
  title: 'Hayalinizdeki Projeyi Birlikte Gerçekleştirelim',
  subtitle: 'Profesyonel ekibimizle iletişime geçin',
  button_text: 'İletişime Geçin',
  button_link: '/iletisim',
};

export default function FooterSettings() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [logoFile, setLogoFile] = useState(null);
  const [stats, setStats] = useState(DEFAULT_STATS);
  const [cta, setCta] = useState(DEFAULT_CTA);

  useEffect(() => {
    api.get('/settings/footer')
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
    api.get('/settings/stats')
      .then(r => {
        const d = r.data || {};
        setStats({
          enabled: d.enabled !== false,
          items: Array.isArray(d.items) && d.items.length > 0 ? d.items : DEFAULT_STATS.items,
        });
      })
      .catch(() => setStats(DEFAULT_STATS));
    api.get('/settings/cta')
      .then(r => {
        const d = r.data || {};
        setCta({
          enabled: d.enabled !== false,
          title: d.title ?? DEFAULT_CTA.title,
          subtitle: d.subtitle ?? DEFAULT_CTA.subtitle,
          button_text: d.button_text ?? DEFAULT_CTA.button_text,
          button_link: d.button_link ?? DEFAULT_CTA.button_link,
        });
      })
      .catch(() => setCta(DEFAULT_CTA));
  }, []);

  const updateStatItem = (idx, key, value) => {
    setStats(s => ({ ...s, items: s.items.map((it, i) => i === idx ? { ...it, [key]: value } : it) }));
  };
  const addStatItem = () => {
    setStats(s => ({ ...s, items: [...s.items, { number: '', label: '', icon: 'building' }] }));
  };
  const removeStatItem = (idx) => {
    setStats(s => ({ ...s, items: s.items.filter((_, i) => i !== idx) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let logoPath = data?.logo_path || '';
      if (logoFile) {
        const fd = new FormData();
        fd.append('file', logoFile);
        const res = await api.post('/uploads', fd);
        logoPath = res.data.path;
      }
      await api.put('/settings/footer', { ...data, logo_path: logoPath });
      await api.put('/settings/stats', { enabled: stats.enabled, items: stats.items });
      await api.put('/settings/cta', cta);
      setData(prev => ({ ...prev, logo_path: logoPath }));
      toast.success('Genel ayarlar güncellendi');
    } catch (err) {
      toast.error('Hata oluştu');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (loading) return <div className="flex justify-center py-20"><div className="w-8 h-8 border-2 border-[hsl(var(--gold))] border-t-transparent rounded-full animate-spin" /></div>;

  return (
    <div data-testid="admin-footer-settings">
      <h1 className="font-heading text-2xl font-semibold mb-6">Genel & Footer Ayarları</h1>
      <form onSubmit={handleSubmit} className="space-y-6">

        {/* Company Branding Section */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Firma Bilgileri</h3>
          <div className="space-y-4">
            <Field label="Firma Adı" value={data?.company_name || ''} onChange={v => setData(d => ({ ...d, company_name: v }))} />
            <Field label="Slogan" value={data?.slogan || ''} onChange={v => setData(d => ({ ...d, slogan: v }))} placeholder="Örneğin: Kaliteli yaşam alanları inşa ediyoruz" />

            {/* Logo Upload */}
            <div>
              <label className="block text-sm font-medium mb-1.5">Firma Logosu</label>
              <div className="flex items-start gap-4">
                <div className="flex flex-col gap-2">
                  <label className="cursor-pointer inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors">
                    <Upload className="w-4 h-4" /> Logo Yükle
                    <input type="file" accept="image/png,image/svg+xml,image/webp" className="hidden" onChange={e => setLogoFile(e.target.files[0])} />
                  </label>
                  <span className="text-xs text-muted-foreground">
                    {logoFile ? logoFile.name : ''}
                  </span>
                </div>
                {data?.logo_path && (
                  <div className="flex flex-col items-center gap-1">
                    <div className="h-14 px-4 py-2 rounded-lg bg-secondary border border-border flex items-center justify-center">
                      <img src={getFileUrl(data.logo_path)} alt="Logo" className="h-10 w-auto object-contain" />
                    </div>
                    <span className="text-[10px] text-muted-foreground">Mevcut logo</span>
                  </div>
                )}
              </div>
              <div className="mt-3 p-3 rounded-lg bg-secondary/50 border border-border">
                <div className="flex items-start gap-2 text-xs text-muted-foreground">
                  <Info className="w-4 h-4 shrink-0 mt-0.5 text-[hsl(var(--gold))]" />
                  <div>
                    <p className="font-medium text-foreground mb-1">Logo Boyut Önerileri:</p>
                    <ul className="space-y-0.5 list-disc list-inside">
                      <li><strong>Önerilen boyut:</strong> 200px x 60px (genişlik x yükseklik)</li>
                      <li><strong>Maksimum boyut:</strong> 400px x 120px</li>
                      <li><strong>Dosya formatı:</strong> PNG (şeffaf arkaplan), SVG veya WebP</li>
                      <li><strong>Dosya boyutu:</strong> Maks. 500KB</li>
                      <li>Şeffaf arkaplanlı (PNG/SVG) logo kullanmanız önerilir. Böylece hem açık hem koyu temada düzgün görünür.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer Description */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Footer İçeriği</h3>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-1.5">Firma Açıklamasi</label>
              <textarea value={data?.description || ''} onChange={e => setData(d => ({ ...d, description: e.target.value }))} rows={3} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))] resize-none" />
            </div>
          </div>
        </div>

        {/* Social Media */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]">
          <h3 className="font-heading text-lg font-semibold mb-4">Sosyal Medya Linkleri</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <Field label="Facebook" value={data?.facebook || ''} onChange={v => setData(d => ({ ...d, facebook: v }))} placeholder="https://facebook.com/..." />
            <Field label="Twitter / X" value={data?.twitter || ''} onChange={v => setData(d => ({ ...d, twitter: v }))} placeholder="https://twitter.com/..." />
            <Field label="Instagram" value={data?.instagram || ''} onChange={v => setData(d => ({ ...d, instagram: v }))} placeholder="https://instagram.com/..." />
            <Field label="LinkedIn" value={data?.linkedin || ''} onChange={v => setData(d => ({ ...d, linkedin: v }))} placeholder="https://linkedin.com/..." />
          </div>
        </div>

        {/* Homepage Stats */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]" data-testid="admin-stats-editor">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Anasayfa İstatistikleri</h3>
              <p className="text-xs text-muted-foreground mt-1">Anasayfadaki rakam kartları (Yıllık Tecrübe, Tamamlanan Proje vb.)</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer shrink-0" data-testid="admin-stats-enabled-toggle">
              <input
                type="checkbox"
                checked={stats.enabled}
                onChange={e => setStats(s => ({ ...s, enabled: e.target.checked }))}
                className="rounded"
              />
              {stats.enabled ? 'Gösteriliyor' : 'Gizli'}
            </label>
          </div>

          {stats.enabled ? (
            <div className="space-y-3">
              {stats.items.map((it, idx) => (
                <div key={idx} className="grid grid-cols-1 md:grid-cols-[120px_1fr_180px_auto] gap-3 items-end p-3 rounded-lg bg-secondary/40 border border-border" data-testid={`admin-stat-row-${idx}`}>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Rakam</label>
                    <input
                      value={it.number}
                      onChange={e => updateStatItem(idx, 'number', e.target.value)}
                      placeholder="20+"
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">Etiket</label>
                    <input
                      value={it.label}
                      onChange={e => updateStatItem(idx, 'label', e.target.value)}
                      placeholder="Yıllık Tecrübe"
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium mb-1 text-muted-foreground">İkon</label>
                    <select
                      value={it.icon || 'building'}
                      onChange={e => updateStatItem(idx, 'icon', e.target.value)}
                      className="w-full px-3 py-2 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                    >
                      {STAT_ICON_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                    </select>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeStatItem(idx)}
                    className="p-2 rounded-lg hover:bg-destructive/10 text-destructive justify-self-start md:justify-self-center"
                    data-testid={`admin-stat-remove-${idx}`}
                    aria-label="Kaldır"
                  >
                    <Trash2 className="w-4 h-4" />
                  </button>
                </div>
              ))}
              <button
                type="button"
                onClick={addStatItem}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg border border-input bg-background text-sm hover:bg-secondary transition-colors"
                data-testid="admin-stat-add"
              >
                <Plus className="w-4 h-4" /> İstatistik Ekle
              </button>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">İstatistik bölümü anasayfada gizli. Göstermek için yukarıdaki anahtarı açın.</p>
          )}
        </div>

        {/* Homepage CTA */}
        <div className="p-6 rounded-2xl bg-card border border-border shadow-[var(--shadow-elev-1)]" data-testid="admin-cta-editor">
          <div className="flex items-center justify-between mb-4">
            <div>
              <h3 className="font-heading text-lg font-semibold">Anasayfa Çağrı Alanı (CTA)</h3>
              <p className="text-xs text-muted-foreground mt-1">Anasayfa altındaki başlık, açıklama ve buton.</p>
            </div>
            <label className="flex items-center gap-2 text-sm cursor-pointer shrink-0" data-testid="admin-cta-enabled-toggle">
              <input
                type="checkbox"
                checked={cta.enabled}
                onChange={e => setCta(c => ({ ...c, enabled: e.target.checked }))}
                className="rounded"
              />
              {cta.enabled ? 'Gösteriliyor' : 'Gizli'}
            </label>
          </div>

          {cta.enabled ? (
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium mb-1.5">Başlık</label>
                <input
                  value={cta.title}
                  onChange={e => setCta(c => ({ ...c, title: e.target.value }))}
                  placeholder="Hayalinizdeki Projeyi Birlikte Gerçekleştirelim"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                  data-testid="admin-cta-title"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1.5">Açıklama</label>
                <textarea
                  value={cta.subtitle}
                  onChange={e => setCta(c => ({ ...c, subtitle: e.target.value }))}
                  rows={2}
                  placeholder="Profesyonel ekibimizle iletişime geçin"
                  className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm resize-none focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                  data-testid="admin-cta-subtitle"
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium mb-1.5">Buton Metni</label>
                  <input
                    value={cta.button_text}
                    onChange={e => setCta(c => ({ ...c, button_text: e.target.value }))}
                    placeholder="İletişime Geçin"
                    className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                    data-testid="admin-cta-button-text"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-1.5">Buton Bağlantısı</label>
                  <input
                    value={cta.button_link}
                    onChange={e => setCta(c => ({ ...c, button_link: e.target.value }))}
                    placeholder="/iletisim veya https://..."
                    className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]"
                    data-testid="admin-cta-button-link"
                  />
                  <p className="text-xs text-muted-foreground mt-1">Site içi (/iletisim) ya da tam URL (https://...)</p>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">Çağrı alanı anasayfada gizli. Göstermek için yukarıdaki anahtarı açın.</p>
          )}
        </div>


        <button type="submit" disabled={isSubmitting} className="px-6 py-2.5 rounded-lg bg-[hsl(var(--gold))] text-[hsl(var(--accent-foreground))] text-sm font-medium hover:bg-[hsl(var(--gold)/0.92)] disabled:opacity-50" data-testid="admin-save-button">
          {isSubmitting ? 'Kaydediliyor...' : 'Kaydet'}
        </button>
      </form>
    </div>
  );
}

const Field = ({ label, value, onChange, placeholder }) => (
  <div>
    <label className="block text-sm font-medium mb-1.5">{label}</label>
    <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder} className="w-full px-3 py-2.5 rounded-lg border border-input bg-background text-sm focus:outline-none focus:ring-2 focus:ring-[hsl(var(--gold))]" />
  </div>
);
